import React from 'react';
import { useTranslation } from 'react-i18next';
import { Workflow } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

function cn(...inputs) {
  return inputs.filter(Boolean).join(' ');
}

const PlanningNavigationCard = ({ sections, sectionColors, activeTab, setActiveTab, currentTab, renderTabContent }) => {
  const { t } = useTranslation();

  return (
    <Card className="border-border shadow-sm">
      <CardContent className="p-8">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Workflow className="h-5 w-5 text-muted-foreground" />
            {t('planning.nav.quickNav')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">{t('planning.nav.selectStep')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          {sections.map((section) => {
            const colors = sectionColors[section.id];
            const sectionTabs = section.tabs;
            const hasActiveTab = sectionTabs.some((t) => t.id === activeTab);

            return (
              <Card
                key={section.id}
                className={cn(
                  'border-2 transition-all hover:shadow-md bg-muted/50',
                  hasActiveTab
                    ? cn(colors.borderLeft, 'border-l-4 shadow-md', colors.bg)
                    : 'border-border hover:border-border'
                )}
              >
                <CardHeader className="p-5 pb-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-foreground">{section.name}</h4>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs font-medium',
                        hasActiveTab
                          ? cn(colors.badgeBg, colors.badgeText, 'border-transparent')
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {t('planning.nav.step', { n: section.step })}
                    </Badge>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    {sectionTabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 transition-all text-left font-medium text-sm',
                            isActive
                              ? colors.buttonActive
                              : cn('bg-card border-border text-foreground', colors.buttonHover)
                          )}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{tab.label}</span>
                          {tab.badge && isActive && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0.5" style={{ backgroundColor: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.30)", color: "inherit" }}>
                              {tab.badge}
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        <Separator className="my-8" />

        {currentTab && (
          <Card className={cn('border-2 mb-6', sectionColors[currentTab.section].bg, sectionColors[currentTab.section].border)}>
            <CardHeader className="py-4 px-5">
              <div className="flex items-center gap-4">
                <div className={cn('p-3 rounded-xl shadow-sm', sectionColors[currentTab.section].buttonActive.split(' ')[0], 'text-white')}>
                  {(() => { const Icon = currentTab.icon; return <Icon className="h-5 w-5" />; })()}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 mb-1">
                    {currentTab.label}
                    <Badge variant="outline" className={cn('text-xs font-medium', sectionColors[currentTab.section].badgeBg, sectionColors[currentTab.section].badgeText, 'border-transparent')}>
                      {t('planning.nav.step', { n: currentTab.step })}
                    </Badge>
                  </CardTitle>
                  <CardDescription className={cn('text-sm', sectionColors[currentTab.section].text)}>
                    {currentTab.description}
                  </CardDescription>
                </div>
                {currentTab.badge && (
                  <Badge className="bg-amber-950/30 text-amber-400 border-amber-900/40 shadow-sm px-3 py-1 font-medium">
                    {currentTab.badge}
                  </Badge>
                )}
              </div>
            </CardHeader>
          </Card>
        )}

        <div className="min-h-[500px]">{renderTabContent()}</div>
      </CardContent>
    </Card>
  );
};

export default PlanningNavigationCard;
